/**
 * Production-safe logging utility for HIPAA-compliant medical records app.
 *
 * Redaction is automatic on every log call (debug / info / warn / error).
 * String arguments are scanned for PHI/PII patterns (email, phone, SSN, MRN-
 * labeled IDs, DOB in common formats, long bearer-token-shaped strings).
 * Object arguments are deep-walked; any key in LOGGER_PII_FIELDS has its value
 * replaced with [REDACTED] before output.
 *
 * The only escape hatch is `logger.unsafeRaw(...)`, which is restricted by
 * lint to functions/src/modules/audit/ — the audit module's job is precisely
 * to log who-accessed-what with real values for HIPAA compliance.
 */
import { trace, PerformanceTrace, FirebasePerformance } from 'firebase/performance';

import { Config } from '@/config/env';

export type LogLevel = 'debug' | 'info' | 'warn' | 'error';

interface LogContext {
  [key: string]: unknown;
}

const activeTraces: Map<string, PerformanceTrace> = new Map();

export const LOGGER_PII_FIELDS = [
  'email',
  'phone',
  'phonenumber',
  'cellphone',
  'ssn',
  'ssnencrypted',
  'ssnmasked',
  'dob',
  'dateofbirth',
  'dateofbirthencrypted',
  'dateofbirthmasked',
  'dateofaccident',
  'firstname',
  'lastname',
  'name',
  'fullname',
  'patientname',
  'clientname',
  'address',
  'streetaddress',
  'mrn',
  'medicalrecordnumber',
  'token',
  'accesstoken',
  'refreshtoken',
  'idtoken',
  'apikey',
  'authorization',
  'cookie',
  'password',
  'secret',
] as const;

const PII_FIELD_SET = new Set<string>(LOGGER_PII_FIELDS);
const REDACTED = '[REDACTED]';
const MAX_REDACT_DEPTH = 20;

const EMAIL_REGEX = /[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}/g;
const SSN_REGEX = /\b\d{3}-?\d{2}-?\d{4}\b/g;
const PHONE_REGEX = /(?:\+?1[-.\s]?)?\(?\b\d{3}\)?[-.\s]\d{3}[-.\s]\d{4}\b/g;
// MRN appears in OCR/provider correspondence as a labeled token; this app has no
// MRN field of its own. Match common label variants followed by an alphanumeric id.
const MRN_REGEX = /\b(?:MRN|Med(?:ical)?\s*Rec(?:ord)?\s*(?:No\.?|Number|#)?)\s*[:#]?\s*[A-Z0-9][A-Z0-9-]{3,}/gi;
const DOB_ISO_REGEX = /\b(?:19|20)\d{2}-(?:0[1-9]|1[0-2])-(?:0[1-9]|[12]\d|3[01])\b/g;
const DOB_SLASH_REGEX = /\b(?:0?[1-9]|1[0-2])\/(?:0?[1-9]|[12]\d|3[01])\/(?:19|20)?\d{2}\b/g;
const DOB_DASH_REGEX = /\b(?:0?[1-9]|1[0-2])-(?:0?[1-9]|[12]\d|3[01])-(?:19|20)?\d{2}\b/g;
const LONG_TOKEN_REGEX = /\b[A-Za-z0-9_-]{32,}\.?[A-Za-z0-9_-]*\.?[A-Za-z0-9_-]*\b/g;
// Structured-log eventCodes (e.g. STATEMENT_RECONCILE_DRIFT_SUBSCRIPTION_ACTIVE)
// are often 32+ chars, which the long-token pattern would swallow — and log
// filters key on them. Random secrets are never ALL_CAPS_SNAKE, so exempting
// that shape doesn't let real tokens through. Mirrors functions/src/utils/logger.ts.
const SCREAMING_SNAKE_REGEX = /^[A-Z][A-Z0-9]*(?:_[A-Z0-9]+)+$/;

function redactString(s: string): string {
  return s
    .replace(EMAIL_REGEX, '[REDACTED_EMAIL]')
    .replace(SSN_REGEX, '[REDACTED_SSN]')
    .replace(PHONE_REGEX, '[REDACTED_PHONE]')
    .replace(MRN_REGEX, '[REDACTED_MRN]')
    .replace(DOB_ISO_REGEX, '[REDACTED_DOB]')
    .replace(DOB_SLASH_REGEX, '[REDACTED_DOB]')
    .replace(DOB_DASH_REGEX, '[REDACTED_DOB]')
    .replace(LONG_TOKEN_REGEX, (m) => (SCREAMING_SNAKE_REGEX.test(m) ? m : '[REDACTED_TOKEN]'));
}

function isPiiKey(key: string): boolean {
  return PII_FIELD_SET.has(key.toLowerCase());
}

/**
 * Deep-redact: returns a structurally-similar copy with PHI/PII stripped.
 *
 * Strings → regex-redacted
 * Objects → keys in LOGGER_PII_FIELDS get [REDACTED]; other keys recurse
 * Arrays → each item recurses
 * Errors → preserve name + message + stack but redact each
 * Circular references → [CIRCULAR]
 */
export function redact(value: unknown, depth = 0, seen?: WeakSet<object>): unknown {
  if (depth > MAX_REDACT_DEPTH) return '[REDACTED_DEPTH_LIMIT]';
  if (value === null || value === undefined) return value;

  const t = typeof value;
  if (t === 'string') return redactString(value as string);
  if (t === 'number' || t === 'boolean' || t === 'bigint' || t === 'symbol') return value;
  if (t === 'function') return '[Function]';

  if (value instanceof Error) {
    const out: Record<string, unknown> = { name: value.name };
    out.message = typeof value.message === 'string' ? redactString(value.message) : value.message;
    if (typeof value.stack === 'string') out.stack = redactString(value.stack);
    return out;
  }

  const tracker = seen ?? new WeakSet<object>();
  if (typeof value === 'object') {
    if (tracker.has(value as object)) return '[CIRCULAR]';
    tracker.add(value as object);
  }

  if (Array.isArray(value)) {
    return value.map((item) => redact(item, depth + 1, tracker));
  }

  if (t === 'object') {
    const result: Record<string, unknown> = {};
    for (const [key, v] of Object.entries(value as Record<string, unknown>)) {
      if (isPiiKey(key)) {
        result[key] = REDACTED;
      } else {
        result[key] = redact(v, depth + 1, tracker);
      }
    }
    return result;
  }

  return value;
}

class Logger {
  private isDevelopment: boolean;
  private isTest: boolean;

  constructor() {
    this.isDevelopment = Config.isDev;
    this.isTest = Config.isTest;
  }

  debug(message: string, context?: LogContext): void {
    if (this.isDevelopment && !this.isTest) {
      console.log(`🔍 [DEBUG] ${redactString(message)}`, redact(context) || '');
    }
  }

  info(message: string, context?: LogContext): void {
    if (this.isDevelopment && !this.isTest) {
      console.log(`ℹ️  [INFO] ${redactString(message)}`, redact(context) || '');
    }
  }

  warn(message: string, context?: LogContext): void {
    if (!this.isTest) {
      console.warn(`⚠️  [WARN] ${redactString(message)}`, redact(context) || '');
    }
  }

  /**
   * Error logging — always goes to console.error in non-test environments and
   * is therefore captured by Cloud Logging / Cloud Error Reporting in prod.
   * All arguments pass through `redact()` first.
   */
  error(message: string, error?: Error | unknown, context?: LogContext): void {
    if (this.isTest) return;
    const safeMessage = redactString(message);
    const errorDetails =
      error instanceof Error
        ? {
            name: error.name,
            message: redactString(error.message),
            stack: this.isDevelopment && error.stack ? redactString(error.stack) : undefined,
          }
        : redact(error);

    console.error(`❌ [ERROR] ${safeMessage}`, errorDetails || '', redact(context) || '');

    if (!this.isDevelopment) {
      this.reportError(safeMessage, errorDetails, redact(context) as LogContext | undefined);
    }
  }

  private reportError(message: string, errorDetails: unknown, context?: LogContext): void {
    const errorReport = {
      severity: 'ERROR',
      message,
      context: {
        ...(context ?? {}),
        // eslint-disable-next-line no-restricted-globals
        url: typeof window !== 'undefined' ? window.location.href : undefined,
        userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : undefined,
        timestamp: new Date().toISOString(),
      },
      error: errorDetails,
    };
    console.error(JSON.stringify(errorReport));
  }

  emulator(message: string): void {
    if (this.isDevelopment && !this.isTest) {
      console.log(`🔧 [EMULATOR] ${redactString(message)}`);
    }
  }

  firebase(message: string, context?: LogContext): void {
    if (this.isDevelopment && !this.isTest) {
      console.log(`🔥 [FIREBASE] ${redactString(message)}`, redact(context) || '');
    }
  }

  /**
   * Legacy alias — redaction now happens automatically on every log call.
   * Returns the redacted form so any existing callers still get back a value
   * safe to interpolate into a log message.
   */
  sanitize(data: unknown): unknown {
    return redact(data);
  }

  /**
   * Bypass redaction. ONLY for use inside functions/src/modules/audit/
   * where logging real user identifiers is the entire point of the audit
   * trail. An ESLint rule (no-restricted-syntax on `unsafeRaw`) forbids this
   * symbol outside the audit module path.
   */
  unsafeRaw = {
    debug: (message: string, ...args: unknown[]): void => {
      if (this.isDevelopment && !this.isTest) console.log(`🔍 [DEBUG-RAW] ${message}`, ...args);
    },
    info: (message: string, ...args: unknown[]): void => {
      if (this.isDevelopment && !this.isTest) console.log(`ℹ️  [INFO-RAW] ${message}`, ...args);
    },
    warn: (message: string, ...args: unknown[]): void => {
      if (!this.isTest) console.warn(`⚠️  [WARN-RAW] ${message}`, ...args);
    },
    error: (message: string, ...args: unknown[]): void => {
      if (!this.isTest) console.error(`❌ [ERROR-RAW] ${message}`, ...args);
    },
  };

  time(label: string): void {
    if (this.isDevelopment && !this.isTest) console.time(`⏱️  ${label}`);
  }

  timeEnd(label: string): void {
    if (this.isDevelopment && !this.isTest) console.timeEnd(`⏱️  ${label}`);
  }

  private performance: unknown | null = null;

  setPerformance(perf: unknown | null): void {
    this.performance = perf;
  }

  /** Independent production trace: callers own its lifetime, so concurrent
   * operations with the same name cannot stop each other's measurements. */
  startIndependentTrace(traceName: string): PerformanceTrace | null {
    try {
      if (this.performance) {
        const perfTrace = trace(this.performance as FirebasePerformance, traceName);
        perfTrace.start();
        return perfTrace;
      }
    } catch {
      // Performance monitoring is always best-effort.
    }
    return null;
  }

  startTrace(traceName: string): PerformanceTrace | null {
    try {
      if (this.performance) {
        const perfTrace = trace(this.performance as unknown as FirebasePerformance, traceName);
        perfTrace.start();
        activeTraces.set(traceName, perfTrace);
        return perfTrace;
      }
    } catch {
      // Performance not available, silently ignore
    }
    return null;
  }

  stopTrace(traceName: string, attributes?: Record<string, string>): void {
    const perfTrace = activeTraces.get(traceName);
    if (perfTrace) {
      if (attributes) {
        Object.entries(attributes).forEach(([key, value]) => {
          perfTrace.putAttribute(key, value);
        });
      }
      perfTrace.stop();
      activeTraces.delete(traceName);
    }
  }

  recordMetric(traceName: string, metricName: string, value: number): void {
    const perfTrace = activeTraces.get(traceName);
    if (perfTrace) {
      perfTrace.putMetric(metricName, value);
    }
  }

  group(label: string): void {
    if (this.isDevelopment && !this.isTest) console.group(label);
  }

  groupEnd(): void {
    if (this.isDevelopment && !this.isTest) console.groupEnd();
  }
}

export const logger = new Logger();

export type { LogContext };
